/**
 * SME plan catalog helpers for /plans (Phase 2).
 */

const FEATURE_LABELS = {
  DASHBOARD: 'Dashboard',
  SECR_REPORT: 'SECR reporting',
  SCIENCE_BASED_TARGETS: 'Science-based targets',
  VSME_FRAMEWORKS: 'VSME frameworks',
  ORGANISATION_HIERARCHY: 'Organisation hierarchy',
  DEPARTMENT_OWNERSHIP: 'Department ownership',
  COMPLETION_DASHBOARD: 'Completion dashboard',
  VERIFICATION_DATA_ROOM: 'Verification data room',
  AI_FACTOR_SUGGEST: 'AI factor suggestions',
  WFH_SURVEY: 'Commuting & WFH Survey',
  CFO_SIGN_OFF: 'CFO sign-off',
};

/**
 * @param {string} key
 * @returns {string}
 */
export const formatFeatureKey = (key) => FEATURE_LABELS[key] || String(key || '').replace(/_/g, ' ').toLowerCase();

/**
 * @param {object} plan — catalog plan from GET /registration/plans
 * @param {'annual'|'monthly'} [interval='annual']
 * @returns {string|null} display price e.g. "£769"
 */
export const formatPlanStartingFrom = (plan, interval = 'annual') => {
  const amount = interval === 'monthly' ? plan?.startingFromMonthly : plan?.startingFromAnnual;
  if (amount == null || amount === '' || Number(amount) <= 0) return null;
  return `£${Number(amount).toLocaleString('en-GB')}`;
};

/**
 * Resolve the plan CTA from backend catalog truth.
 * `buyable` is descriptive only; `canCheckout` is the Checkout gate.
 *
 * @param {object} plan
 * @returns {'checkout_unlocked'|'checkout'|'contact_sales'|'hidden'}
 */
export const planCta = (plan) => {
  if (plan?.canCheckout) return plan.unlockedByPromo ? 'checkout_unlocked' : 'checkout';
  if (plan?.contactSales) return 'contact_sales';
  return 'hidden';
};

/**
 * Build human-readable include bullets for a plan card.
 * @param {object} plan
 * @returns {string[]}
 */
export const buildPlanIncludeBullets = (plan) => {
  const includes = plan?.includes || {};
  const bullets = [];

  (includes.featureKeys || []).forEach((key) => {
    bullets.push(formatFeatureKey(key));
  });

  if (includes.scope3Categories != null) {
    bullets.push(`${includes.scope3Categories} Scope 3 categories`);
  }
  if (includes.users != null) {
    bullets.push(`Up to ${includes.users} users`);
  } else if (includes.users === null && plan?.id === 'govern') {
    bullets.push('Unlimited users');
  }
  if (includes.sites != null) {
    bullets.push(`Up to ${includes.sites} sites`);
  }
  if (includes.invoiceEntriesPerYear != null) {
    bullets.push(`Up to ${Number(includes.invoiceEntriesPerYear).toLocaleString('en-GB')} invoice entries / year`);
  }

  return bullets;
};

/**
 * Mid-point FTE for a band id (from catalog fteBands or known defaults).
 * @param {string} fteBandId
 * @param {Array<{ id: string, midFte?: number }>} [fteBands]
 * @returns {number|null}
 */
export const midFteForBand = (fteBandId, fteBands = []) => {
  const fromCatalog = fteBands.find((b) => b.id === fteBandId);
  if (fromCatalog?.midFte != null) return fromCatalog.midFte;
  const defaults = {
    '1-10': 5,
    '11-50': 25,
    '51-100': 75,
    '101-200': 150,
    '201-250': 225,
    '250-plus': 250,
  };
  return defaults[fteBandId] ?? null;
};
